import { dataArraySieve } from "@smp/utils";
import { format, isValid } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useEffectOnce } from "react-use";

const serializeUserData = (
  data: unknown,
): Array<{
  content: string;
  title: string;
  link?: string;
  key?: string;
}> => {
  if (typeof data !== "object" || data === null) {
    return [];
  }

  return dataArraySieve(
    Object.keys(data).map((field) => {
      const value = (() => {
        const v = data[field];

        if (typeof v === "boolean") {
          return v ? "Да" : "Нет";
        }

        if (v === undefined) {
          return undefined;
        }

        const date = typeof v === "string" ? new Date(v) : undefined;
        if (date && isValid(date)) {
          return format(date, "dd.MM.yyyy HH:mm");
        }

        return String(v);
      })();

      if (value === undefined) {
        return undefined;
      }

      return {
        content: value,
        title: field,
      };
    }),
  );
};

export const useJivo = (enabled: boolean) => {
  const initedListeners = useRef<Function[]>([]);
  const [isInited, setIsInited] = useState<boolean>(false);

  const hide = () => {
    try {
      if (window.jivo_api) {
        window.jivo_api.close();
      } else {
        initedListeners.current.push(() => {
          window.jivo_api && window.jivo_api.close();
        });
      }
    } catch (e) {}
  };

  const show = () => {
    try {
      if (window.jivo_api) {
        window.jivo_api.open();
      } else {
        initedListeners.current.push(() => {
          window.jivo_api && window.jivo_api.open();
        });
      }
    } catch (e) {}
  };

  const showNewMessage = (message: string) => {
    if (window.jivo_api) {
      try {
        window.jivo_api.open();
        window.jivo_api.sendMessage(message);
      } catch (e) {}
    } else {
      initedListeners.current.push(() => {
        if (window.jivo_api) {
          try {
            window.jivo_api.open();
            window.jivo_api.sendMessage(message);
          } catch (e) {}
        }
      });
    }
  };

  const update = (options: any) => {
    const { email, name, jivo_token, ...spread } = options;

    const contactInfo = {
      email,
      name: email,
      phone: undefined,
    };
    const customData = serializeUserData(spread);

    const call = () => {
      if (window.jivo_api) {
        const jivoApi = window.jivo_api;

        try {
          jivoApi.clearHistory();
        } catch (e) {}

        window.jivo_onLoadCallback = () => {
          try {
            jivoApi.setUserToken(jivo_token || "");
            jivoApi.setContactInfo(contactInfo);
            jivoApi.setCustomData(customData);
          } catch (e) {}
        };

        try {
          window.jivo_destroy?.();
          window.jivo_init?.();
        } catch (e) {}
      }
    };

    if (window.jivo_api) {
      call();
    } else {
      initedListeners.current.push(call);
    }
  };

  useEffect(() => {
    if (isInited) {
      while (initedListeners.current.length) {
        const callback = initedListeners.current.shift();

        callback && callback();
      }
    }
  }, [isInited]);

  useEffectOnce(() => {
    (async () => {
      if (!enabled) {
        return;
      }

      if (window.jivo_api) {
        return setIsInited(true);
      }

      window.jivo_onLoadCallback = () => {
        setIsInited(true);
      };
    })();
  });

  return {
    hide,
    isInited,
    show,
    showNewMessage,
    update,
  };
};
